import { Button, IconButton } from '@fountain/fountain-ui-components';
import { Icon, XOutlineIcon } from '@fountain/ripple/icon';
import { Typography } from '@material-ui/core';
import complianceAppIcon from 'images/compliance-app-icon.svg';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePostHog } from 'hooks/usePostHog';

import { CUE_COMPLIANCE_UPSELL_CTA } from './analytics-events';
import { ComplianceUpsellModal } from './ComplianceUpsellModal';
import messages from './messages';
import { useStyles } from './styles';

export interface ComplianceUpsellStageCardProps {
  openingTitle: string;
  stageTitle: string;
}

// Promo card for the Compliance upsell, matching the Figma handoff
// (node 12381:195402). Rendered directly below an eligible data-collection stage
// in the workflow editor's stage list; StageColumn owns the gating
// (`show_compliance_upsell` on the stage, the `wx-compliance-upsell` flag, and
// WxCueRootHost's mount gate so "Start free trial" has a panel to open).
//
// "Discover" opens ComplianceUpsellModal, which carries the pitch. The modal's
// "Start free trial" is the card's old Discover action — it opens the Cue panel
// with a prompt naming this workflow and stage. Everything after that —
// matching the file questions to compliance document types, asking for consent,
// and transforming the workflow — belongs to the Compliance Upsell Agent
// (CMP-1810), not to this component.
//
// Dismissal is per-render only and intentionally not persisted; if it should
// survive a reload that needs a real preference store. Closing the modal
// leaves the card in place and is re-openable.
export const ComplianceUpsellStageCard: React.FC<
  ComplianceUpsellStageCardProps
> = ({ openingTitle, stageTitle }) => {
  const classes = useStyles();
  const intl = useIntl();
  const posthog = usePostHog();
  const [dismissed, setDismissed] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // The card's impression, and the first step of its view-to-click funnel.
  // CueCtaButton fires its own view on mount, so it counts modal opens, not
  // this.
  useEffect(() => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.viewEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
  }, [posthog]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.closeEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
    setDismissed(true);
  };

  const handleDiscover = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_CTA.clickEvent, {
      cta_id: CUE_COMPLIANCE_UPSELL_CTA.ctaId,
    });
    setModalOpen(true);
  };

  return (
    <div className={classes.card}>
      <img alt="" className={classes.icon} src={complianceAppIcon} />

      <div className={classes.content}>
        <div className={classes.headerRow}>
          <div>
            <Typography className={classes.title} variant="body1">
              {intl.formatMessage(messages.title)}
            </Typography>
            <Typography className={classes.subtitle} variant="body2">
              {intl.formatMessage(messages.subtitle)}
            </Typography>
          </div>

          <IconButton
            aria-label={intl.formatMessage(messages.dismissAriaLabel)}
            className={classes.dismissButton}
            onClick={handleDismiss}
            size="small"
          >
            <Icon icon={XOutlineIcon} size="sm" color="inherit" />
          </IconButton>
        </div>

        <div className={classes.discoverButtonWrapper}>
          <Button onClick={handleDiscover} size="small" type="secondary">
            {intl.formatMessage(messages.discover)}
          </Button>
        </div>
      </div>

      {/* Mounted only while open, so the modal's view event counts opens and
          the closed dialog leaves no DOM (MUI keeps a closing Dialog mounted
          through its exit transition). */}
      {modalOpen && (
        <ComplianceUpsellModal
          onClose={() => setModalOpen(false)}
          open={modalOpen}
          openingTitle={openingTitle}
          stageTitle={stageTitle}
        />
      )}
    </div>
  );
};
